import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Drawer, Tooltip, useMediaQuery} from '@mui/material';
import {useLocation, useNavigate} from "react-router-dom";
import {collectionService} from '../../../services/collection.service';
import {useTranslation} from 'react-i18next';

import Divider from '@mui/material/Divider';
import {
    ConversationHistorySection,
    ConversationList,
    HistoryHeader,
    LogoBox,
    MobileMenuButton,
    OcrBatchContent,
    OcrBatchHistorySection,
    SidebarContent,
    SidebarHeader,
    StyledIconButton,
    StyledSectionHeader,
    ToolItem,
    ToolsHeader,
    ToolsList
} from './Sidebar.styled';

import {brand} from "../../../theme/brand";
import {ReactComponent as ChevronRightSvg} from "../../../assets/icons/chevron-right.svg";
import {ReactComponent as EditSvg} from "../../../assets/icons/edit.svg";
import {ReactComponent as MenuSvg} from "../../../assets/icons/menu.svg";
import {ReactComponent as OcrSidebarLogo} from "../../../assets/icons/ocr_sidebar_logo.svg";
import {ReactComponent as BookOcrLogo} from "../../../assets/icons/book_ocr.svg";
import {ReactComponent as DocumentsOcrLogo} from "../../../assets/icons/documents_ocr.svg";
import {ReactComponent as MicrophoneLogo} from "../../../assets/icons/microphone.svg";
import CropFreeIcon from '@mui/icons-material/CropFree';

import {useAppConstants} from '../../../config/appConstants';
import {PageTitleKeys, pageTitles} from '../../../config/routeConstants';
import {useSnackbar} from "../../../contexts/SnackbarContext";
import {useAuth} from "../../../contexts/AuthContext";
import {useUser} from "../../../contexts/UserContext";
import {ConversationResponse, DataSource} from '../../../models/models';
import {parseError} from "../../../errors/http.service";

import AdminDropdown from "./components/AdminDropdown";
import ConversationItem from "./components/ConversationItem";
import {useOcrHistory} from "../../../hooks/useOcr";
import OcrBatchItem from "./components/OcrBatchItem";
import SidebarProfile from "./components/SidebarProfile";
import SidebarReportProblem from "./components/SidebarReportProblem";
import SidebarDataSources from "./components/SidebarDataSources";
import {conversationService} from "../../../services/conversation.service";
import {USER_ROLES} from "../../../models/users.models";
import {useFeatureConfig} from "../../../contexts/FeatureConfigContext";
import {FEATURE_KEYS} from "../../../models/admin-config.models";

type ConversationSectionsType = {
    PINNED: string,
    TODAY: string,
    YESTERDAY: string,
    LAST_7_DAYS: string,
    OLDER: string
};

const buildConversationGroups = (
    conversations: ConversationResponse[],
    sections: ConversationSectionsType,
) => {
    const now = new Date();
    const groups: Record<string, ConversationResponse[]> = {
        [sections.PINNED]: [],
        [sections.TODAY]: [],
        [sections.YESTERDAY]: [],
        [sections.LAST_7_DAYS]: [],
        [sections.OLDER]: [],
    };

    const sorted = [...conversations].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    for (const convo of sorted) {
        if (convo.pinned) {
            groups[sections.PINNED].push(convo);
            continue;
        }

        const startOfDay = (date: Date) =>
            new Date(date.getFullYear(), date.getMonth(), date.getDate());

        const convoDate = new Date(convo.createdAt);

        const diffDays = Math.floor(
            (startOfDay(now).getTime() - startOfDay(convoDate).getTime()) / (1000 * 60 * 60 * 24)
        );

        if (diffDays === 0) {
            groups[sections.TODAY].push(convo);
        } else if (diffDays === 1) {
            groups[sections.YESTERDAY].push(convo);
        } else if (diffDays <= 7) {
            groups[sections.LAST_7_DAYS].push(convo);
        } else {
            groups[sections.OLDER].push(convo);
        }
    }

    return groups;
};

interface SidebarProps {
    isOpen: boolean;
    onToggle: () => void
}

// TODO : fix conversationId type to number
// TODO: verify if rendering can be optimized here
const Sidebar = ({isOpen, onToggle}: SidebarProps) => {
    const {t} = useTranslation();
    const {showSnackbar} = useSnackbar();
    const navigate = useNavigate();
    const location = useLocation();

    const {DEFAULT_CONVERSATION_TITLE_PREFIX, CONVERSATION_GROUP_SECTIONS} = useAppConstants()

    const isMobile = useMediaQuery('(max-width: 959px)');

    const {user, fetchUserData, refreshConversations} = useUser();
    const {isAdmin} = useAuth();
    const {isFeatureEnabled} = useFeatureConfig();

    const isOcrEnabled = isFeatureEnabled(FEATURE_KEYS.OCR_KEY);
    const isTranslationEnabled = isFeatureEnabled(FEATURE_KEYS.TRANSLATION_KEY);
    const isDocumentAnalysisEnabled = isFeatureEnabled(FEATURE_KEYS.DOCUMENT_ANALYSIS_KEY);
    const isDocumentDigitizationEnabled = isFeatureEnabled(FEATURE_KEYS.DOCUMENT_DIGITIZATION_KEY);

    const batches = useOcrHistory();

    const [conversations, setConversations] = useState<ConversationResponse[]>([]);
    const [dataSources, setDataSources] = useState<DataSource[]>([]);

    const fetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const isActive = isDocumentAnalysisEnabled && location.pathname === "/chat";
    const isChatActive = isDocumentAnalysisEnabled && (location.pathname === "/chat" || location.pathname.startsWith("/chat/"));

    const isOcrActive = isOcrEnabled && (location.pathname === "/ocr/batch" || location.pathname.startsWith("/ocr/batch/"));
    const isTranslatorActive = isTranslationEnabled && location.pathname === "/text-translator";
    const isDigitizationActive = isDocumentDigitizationEnabled
        && (location.pathname === "/digitization" || location.pathname.startsWith("/digitization/"));

    const currentOcrBatchId = useMemo(() => {
        const pathParts = location.pathname.split('/');

        if (location.pathname.startsWith('/ocr/batch/') && pathParts.length === 4) {
            const id = parseInt(pathParts[3], 10);
            return isNaN(id) ? null : id;
        }
        return null;
    }, [location.pathname]);

    const currentChatId = useMemo(() => {
        const pathParts = location.pathname.split('/');
        if (location.pathname.startsWith('/chat/') && pathParts.length === 3) {
            const id = parseInt(pathParts[2], 10);
            return isNaN(id) ? null : id;
        }
        return null;
    }, [location.pathname]);

    const isSystemAdmin = isAdmin || user?.role === USER_ROLES.SYSTEM_ADMIN;

    useEffect(() => {
        if (!user) void fetchUserData();
    }, [user]);

    // todo: refactor all '/documents/by-collection' instances to control 'size' and 'omitCache' arguments for that
    //       single source of truth.

    const fetchDataSources = useCallback(async () => {
        if (!isDocumentAnalysisEnabled) return;

        try {
            const data = await collectionService.fetchCollections(0, 200, true);
            const sortedDataSources = [...(data?.content ?? [])].sort((a, b) => {
                return new Date(b.lastModificationDate).getTime() - new Date(a.lastModificationDate).getTime()
            });
            setDataSources(sortedDataSources.slice(0, 3));
        } catch (error) {
            showSnackbar(parseError(error), "error");
            setDataSources([]);
        }
    }, [showSnackbar, isDocumentAnalysisEnabled]);

    const fetchConversations = useCallback(async () => {
        if (!isDocumentAnalysisEnabled) return;

        try {
            const conversations = await conversationService.fetchConversationsHistory();
            setConversations(conversations);
        } catch (error) {
            showSnackbar(parseError(error), "error");
        }
    }, [showSnackbar, isDocumentAnalysisEnabled]);

    const groupedConversations = useMemo(
        () => buildConversationGroups(conversations, CONVERSATION_GROUP_SECTIONS),
        [conversations, CONVERSATION_GROUP_SECTIONS]
    );

    const groupedBatches = useMemo(() => {
        if (!batches || !isOcrEnabled) return { pinned: [], unpinned: [] };

        const pinned = batches
            .filter(b => b.pinned)
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

        const unpinned = batches
            .filter(b => !b.pinned)
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

        return { pinned, unpinned };
    }, [batches, isOcrEnabled]);

    const handleLogoClick = () => {
        navigate('/');
    };

    useEffect(() => {
        if (!isDocumentAnalysisEnabled) return;

        if (currentChatId !== null) {
            const activeConversation = conversations.find(convo => convo.id === currentChatId);
            if (activeConversation?.title) {
                document.title = activeConversation.title;
            } else if (activeConversation) {
                document.title = `${DEFAULT_CONVERSATION_TITLE_PREFIX}${activeConversation.id}`;
            } else {
                document.title = t(pageTitles[PageTitleKeys.ChatWithId]);
            }
        } else if (location.pathname === '/chat') {
            document.title = t(pageTitles[PageTitleKeys.Chat]);
        }
    }, [currentChatId, conversations, location.pathname, isDocumentAnalysisEnabled, t]);

    useEffect(() => {
        if (!isOpen || !isDocumentAnalysisEnabled) return;

        if (fetchTimeoutRef.current) {
            clearTimeout(fetchTimeoutRef.current);
        }


        fetchTimeoutRef.current = setTimeout(() => {
            void fetchConversations();
            void fetchDataSources();
        }, 300);

        return () => {
            if (fetchTimeoutRef.current) {
                clearTimeout(fetchTimeoutRef.current);
            }
        };
    }, [isOpen, isDocumentAnalysisEnabled, refreshConversations, fetchConversations, fetchDataSources]);


    return (
        <>
            {isMobile && !isOpen && (
                <Tooltip title={t('sidebar.expand')}>
                    <MobileMenuButton onClick={onToggle}>
                        <MenuSvg />
                    </MobileMenuButton>
                </Tooltip>
            )}
            <Drawer
                variant={isMobile ? "temporary" : "permanent"}
                open={isOpen}
                onClose={isMobile ? onToggle : undefined}
                ModalProps={{
                    keepMounted: true,
                }}
            >
                <SidebarHeader $isOpen={isOpen}>
                    {isOpen &&
                        <LogoBox onClick={handleLogoClick}>
                            <img src={brand.logo} alt={"Logo"}/>
                        </LogoBox>
                    }
                    <Tooltip title={isOpen ? t('sidebar.collapse') : t('sidebar.expand')}>
                        <StyledIconButton onClick={onToggle}>
                            <MenuSvg />
                        </StyledIconButton>
                    </Tooltip>
                </SidebarHeader>

                <SidebarContent open={isOpen}>
                    {isDocumentAnalysisEnabled && (
                        <>
                            <ToolItem $active={isActive} onClick={() => navigate('/chat')} $open={isOpen}>
                                <EditSvg/>
                                {isOpen && (
                                    <>
                                        <span>{t('sidebar.chatStartMessage')}</span>
                                        <ChevronRightSvg style={{marginLeft: 'auto'}}/>
                                    </>)
                                }
                            </ToolItem>

                            <Divider />
                        </>
                    )}

                    <ToolsList>
                        {isOpen && (
                            <ToolsHeader variant="h6">
                                {t('tools.title')}
                            </ToolsHeader>)
                        }

                        {isOcrEnabled && (
                            <ToolItem $active={isOcrActive} $open={isOpen} onClick={() => navigate('/ocr/batch')}>
                                <OcrSidebarLogo/>
                                <span>{t('tools.ocr')}</span>
                            </ToolItem>
                        )}

                        {isTranslationEnabled && (
                            <ToolItem $active={isTranslatorActive} $open={isOpen} onClick={() => navigate('/text-translator')}>
                                <BookOcrLogo />
                                <span>{t('tools.textTranslator')}</span>
                            </ToolItem>
                        )}

                        {isDocumentAnalysisEnabled && (
                            <ToolItem $active={isChatActive} $open={isOpen} onClick={() => navigate('/chat')}>
                                <DocumentsOcrLogo />
                                <span>{t('tools.chat')}</span>
                            </ToolItem>
                        )}

                        {isDocumentDigitizationEnabled && (
                            <ToolItem $active={isDigitizationActive} $open={isOpen} onClick={() => navigate('/digitization')}>
                                <CropFreeIcon />
                                <span>{t('tools.documentsDigitization')}</span>
                            </ToolItem>
                        )}
                    </ToolsList>

                    <Divider />

                    {isSystemAdmin && <AdminDropdown isOpen={isOpen}/>}

                    {isSystemAdmin && <Divider />}

                    {isOcrActive && (groupedBatches.pinned.length > 0 || groupedBatches.unpinned.length > 0) && (
                        <OcrBatchContent>
                            {isOpen && <HistoryHeader variant='h6'>{t('sidebar.ocrHistory')}</HistoryHeader>}

                            <OcrBatchHistorySection>
                                {groupedBatches.pinned.length > 0 && (
                                    <>
                                        {isOpen && <StyledSectionHeader variant='body2'>{t('constants.pinned')}</StyledSectionHeader>}
                                        {groupedBatches.pinned.map(batch => (
                                            <OcrBatchItem key={batch.id} currentBatchId={currentOcrBatchId} batch={batch} isOpen={isOpen} />
                                        ))}
                                    </>
                                )}

                                {groupedBatches.unpinned.map(batch => (
                                    <OcrBatchItem key={batch.id} currentBatchId={currentOcrBatchId} batch={batch} isOpen={isOpen} />
                                ))}
                            </OcrBatchHistorySection>
                        </OcrBatchContent>
                    )}

                    {isOpen && isChatActive && dataSources.length > 0 && (
                        <>
                            <SidebarDataSources dataSources={dataSources} isSystemAdmin={isSystemAdmin} />
                            <Divider />
                        </>
                    )}

                    {isOpen && isChatActive && conversations?.length > 0 && (
                        <ConversationHistorySection>
                            <HistoryHeader variant='h6'>{t('sidebar.history')}</HistoryHeader>

                            <ConversationList open={isOpen}>
                                {Object.entries(groupedConversations).map(([period, periodConversations]) => {
                                    if (!periodConversations.length) return null;

                                    return (
                                        <div key={period}>
                                            <StyledSectionHeader variant='body2'>{period}</StyledSectionHeader>

                                            {periodConversations.map((convo) => (
                                                <ConversationItem
                                                    key={convo.id}
                                                    convo={convo}
                                                    isOpen={isOpen}
                                                    currentChatId={currentChatId}
                                                    onRefresh={fetchConversations}
                                                />
                                            ))}
                                        </div>
                                    );
                                })}
                            </ConversationList>
                        </ConversationHistorySection>
                    )}

                </SidebarContent>

                <SidebarReportProblem isOpen={isOpen} />

                <SidebarProfile user={user} isOpen={isOpen} />
            </Drawer>
        </>
    );
};

export default Sidebar;
